import React, { useEffect, useState } from "react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";
import { useQuery, useAction } from "wasp/client/operations";
// AppFrame removed: route-level layout wraps admin pages
import { Card } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { getFeatureFlags, updateFeatureFlags } from "wasp/client/operations";

interface FeatureFlag {
  key: string;
  enabled: boolean;
  rollout: number;
  description: string;
  updatedAt: string;
  source?: "environment" | "database";
}

interface FeatureFlagsData {
  flags: Record<string, FeatureFlag>;
  metadata: {
    dbFlagsCount: number;
    envFlagsCount: number;
    totalFlags: number;
    lastUpdated: string;
  };
}

interface UpdateResult {
  success: boolean;
  updated: number;
  errorsCount: number;
  results: Array<{
    key: string;
    success: boolean;
    flag?: any;
  }>;
  errors?: Array<{
    key: string;
    error: string;
  }>;
}

export function AdminFeatureFlags() {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Boolean((user as any)?.isAdmin);
  const [flags, setFlags] = useState<Record<string, FeatureFlag>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  useEffect(() => {
    if (user && !isAdmin) navigate("/");
  }, [user, isAdmin]);

  const {
    data,
    isLoading: queryLoading,
    error,
    refetch,
  } = useQuery(getFeatureFlags, undefined, {
    enabled: isAdmin,
  });

  useEffect(() => {
    if (data?.flags) {
      setFlags(data.flags);
      setIsLoading(false);
    }
  }, [data]);

  const doUpdateFeatureFlags = useAction(updateFeatureFlags);

  const handleToggleFlag = async (key: string, enabled: boolean) => {
    setIsUpdating(key);
    try {
      const updatedFlags = {
        [key]: {
          enabled,
          rollout: flags[key]?.rollout || 100,
          description: flags[key]?.description || `Feature flag: ${key}`,
        },
      };

      const result = (await doUpdateFeatureFlags({
        flags: updatedFlags,
      })) as UpdateResult;

      if (result.success) {
        // Update local state
        setFlags((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            enabled,
            updatedAt: new Date().toISOString(),
            source: "database",
          },
        }));
      } else {
        alert(
          `Failed to update ${key}: ${result.errors?.[0]?.error || "Unknown error"}`,
        );
      }
    } catch (error: any) {
      alert(`Failed to update ${key}: ${error.message}`);
    } finally {
      setIsUpdating(null);
    }
  };

  const handleRolloutChange = async (key: string, rollout: number) => {
    setIsUpdating(key);
    try {
      const updatedFlags = {
        [key]: {
          enabled: flags[key]?.enabled || false,
          rollout,
          description: flags[key]?.description || `Feature flag: ${key}`,
        },
      };

      const result = (await doUpdateFeatureFlags({
        flags: updatedFlags,
      })) as UpdateResult;

      if (result.success) {
        // Update local state
        setFlags((prev) => ({
          ...prev,
          [key]: {
            ...prev[key],
            rollout,
            updatedAt: new Date().toISOString(),
            source: "database",
          },
        }));
      } else {
        alert(
          `Failed to update ${key}: ${result.errors?.[0]?.error || "Unknown error"}`,
        );
      }
    } catch (error: any) {
      alert(`Failed to update ${key}: ${error.message}`);
    } finally {
      setIsUpdating(null);
    }
  };

  if (error && (error as any)?.message?.includes("403")) {
    return (
        <div className="p-6 text-red-600">Access denied: admin only.</div>
    );
  }

  if (isLoading || queryLoading) {
    return (
        <div className="space-y-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-martinique dark:text-white">
              Feature Flags
            </h1>
            <p className="mt-2 text-martinique/70 dark:text-white/70">
              Loading feature flags...
            </p>
          </div>
          <div className="animate-pulse space-y-6">
            <div className="h-32 rounded-xl bg-stone dark:bg-darkcard"></div>
            <div className="h-32 rounded-xl bg-stone dark:bg-darkcard"></div>
          </div>
        </div>
    );
  }

  const environmentFlags = Object.entries(flags).filter(
    ([_, flag]) => flag.source === "environment",
  );
  const databaseFlags = Object.entries(flags).filter(
    ([_, flag]) => flag.source !== "environment",
  );

  return (
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-martinique dark:text-white">
            Feature Flags
          </h1>
          <p className="mt-2 text-martinique/70 dark:text-white/70">
            Manage feature flags and rollout percentages. Environment flags take
            precedence over database flags.
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-stone p-6 shadow-card transition-all duration-200 dark:bg-darkcard">
            <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
              Total Flags
            </div>
            <div className="text-3xl font-bold text-martinique dark:text-white">
              {Object.keys(flags).length}
            </div>
          </Card>
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand p-6 shadow-card transition-all duration-200 dark:bg-darkcard">
            <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
              Environment Flags
            </div>
            <div className="text-3xl font-bold text-martinique dark:text-white">
              {environmentFlags.length}
            </div>
          </Card>
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand p-6 shadow-card transition-all duration-200 dark:bg-darkcard">
            <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
              Database Flags
            </div>
            <div className="text-3xl font-bold text-martinique dark:text-white">
              {databaseFlags.length}
            </div>
          </Card>
          <Card className="hover:shadow-cardHover rounded-xl border-0 bg-sand p-6 shadow-card transition-all duration-200 dark:bg-darkcard">
            <div className="mb-3 text-sm font-medium text-martinique/60 dark:text-white/60">
              Enabled
            </div>
            <div className="text-3xl font-bold text-martinique dark:text-white">
              {Object.values(flags).filter((f) => f.enabled).length}
            </div>
          </Card>
        </div>

        {/* Environment Flags */}
        {environmentFlags.length > 0 && (
          <Card className="rounded-xl border-0 bg-sand p-8 shadow-card dark:bg-darkcard">
            <div className="mb-6 text-xl font-medium text-martinique dark:text-white">
              Environment Flags
              <Badge variant="destructive" className="ml-3 text-xs">
                Environment Override
              </Badge>
            </div>
            <div className="space-y-4">
              {environmentFlags.map(([key, flag]) => (
                <div
                  key={key}
                  className="rounded-lg border border-orange-200 bg-orange-50 p-6 dark:border-orange-800 dark:bg-orange-950/20"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-3">
                        <h3 className="font-medium text-martinique dark:text-white">
                          {key}
                        </h3>
                        <Badge
                          variant={flag.enabled ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {flag.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Environment
                        </Badge>
                      </div>
                      <p className="mb-2 text-sm text-martinique/70 dark:text-white/70">
                        {flag.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-martinique/60 dark:text-white/60">
                        <span>Rollout: {flag.rollout}%</span>
                        <span>
                          Updated: {new Date(flag.updatedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-martinique/70 dark:text-white/70">
                        {flag.enabled ? "ON" : "OFF"}
                      </span>
                      <Switch
                        checked={flag.enabled}
                        disabled={true}
                        className="cursor-not-allowed opacity-50"
                      />
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-orange-600 dark:text-orange-400">
                    Environment flags cannot be modified from the admin panel
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Database Flags */}
        {databaseFlags.length > 0 && (
          <Card className="rounded-xl border-0 bg-sand p-8 shadow-card dark:bg-darkcard">
            <div className="mb-6 text-xl font-medium text-martinique dark:text-white">
              Database Flags
              <Badge variant="default" className="ml-3 text-xs">
                Editable
              </Badge>
            </div>
            <div className="space-y-4">
              {databaseFlags.map(([key, flag]) => (
                <div
                  key={key}
                  className="rounded-lg border border-white/5 bg-lynx p-6 dark:bg-martinique"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-3">
                        <h3 className="font-medium text-martinique dark:text-white">
                          {key}
                        </h3>
                        <Badge
                          variant={flag.enabled ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {flag.enabled ? "Enabled" : "Disabled"}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Database
                        </Badge>
                      </div>
                      <p className="mb-2 text-sm text-martinique/70 dark:text-white/70">
                        {flag.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-martinique/60 dark:text-white/60">
                        <span>Rollout: {flag.rollout}%</span>
                        <span>
                          Updated: {new Date(flag.updatedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor={`rollout-${key}`}
                          className="text-sm text-martinique/70 dark:text-white/70"
                        >
                          {flag.rollout}%
                        </label>
                        <input
                          id={`rollout-${key}`}
                          type="range"
                          min="0"
                          max="100"
                          step="10"
                          value={flag.rollout}
                          onChange={(e) =>
                            handleRolloutChange(key, parseInt(e.target.value))
                          }
                          disabled={isUpdating === key}
                          className="w-20"
                        />
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-martinique/70 dark:text-white/70">
                          {flag.enabled ? "ON" : "OFF"}
                        </span>
                        <Switch
                          checked={flag.enabled}
                          onCheckedChange={(checked) =>
                            handleToggleFlag(key, checked)
                          }
                          disabled={isUpdating === key}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Metadata */}
        {data?.metadata && (
          <Card className="rounded-xl border-0 bg-sand p-8 shadow-card dark:bg-darkcard">
            <div className="mb-6 text-xl font-medium text-martinique dark:text-white">
              System Information
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
              <div>
                <div className="text-martinique/60 dark:text-white/60">
                  Database Flags
                </div>
                <div className="font-medium text-martinique dark:text-white">
                  {data.metadata.dbFlagsCount}
                </div>
              </div>
              <div>
                <div className="text-martinique/60 dark:text-white/60">
                  Environment Flags
                </div>
                <div className="font-medium text-martinique dark:text-white">
                  {data.metadata.envFlagsCount}
                </div>
              </div>
              <div>
                <div className="text-martinique/60 dark:text-white/60">
                  Total Flags
                </div>
                <div className="font-medium text-martinique dark:text-white">
                  {data.metadata.totalFlags}
                </div>
              </div>
              <div>
                <div className="text-martinique/60 dark:text-white/60">
                  Last Updated
                </div>
                <div className="font-medium text-martinique dark:text-white">
                  {new Date(data.metadata.lastUpdated).toLocaleString()}
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
  );
}

export default AdminFeatureFlags;
